"use client";

/**
 * SetupLayout Component
 *
 * Split-screen layout wrapper for the setup flow:
 * - Desktop (lg+): 55% form (left) / 45% social proof (right)
 * - Mobile: Full-width form, social proof hidden
 * - Payment step: Full-width form, social proof hidden
 *
 * IMPORTANT: Layout changes are INSTANT (no animation).
 * Only content animates via BlurTransition. This prevents
 * horrible mid-transition states when switching layouts.
 *
 * Header design (Fyxer.ai style):
 * - Row 1: Back button (left) | Logo (centered) | empty (right)
 * - Row 2: Full-width segmented progress bar (only from step 1+)
 */
import { c as _c } from "react/compiler-runtime";
import { ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { T, useGT } from "gt-next";
import SocialProofPanel from "./SocialProofPanel";
import SetupHelpLink from "./SetupHelpLink";
import type { SetupStep } from "../types";
interface SetupLayoutProps {
  children: ReactNode;
  /** Current step for the indicator */
  currentStep: SetupStep;
  /** Total number of steps */
  totalSteps: number;
  /** Whether to show the back button */
  showBackButton?: boolean;
  /** Back button handler */
  onBack?: () => void;
  /** Custom label for the back button (default: "Back") */
  backButtonLabel?: string;
  /** Whether user is logged in (affects step count) */
  isLoggedIn?: boolean;
  /** Whether to use full-width layout (hides social proof panel) */
  fullWidth?: boolean;
}

// Shared transition config
const TRANSITION = {
  duration: 0.25,
  ease: [0.22, 1, 0.36, 1] as const
};
export default function SetupLayout(t0) {
  const $ = _c(51);
  const {
    children,
    currentStep,
    totalSteps,
    showBackButton: t1,
    onBack,
    backButtonLabel,
    isLoggedIn: t2,
    fullWidth: t3
  } = t0;
  const showBackButton = t1 === undefined ? true : t1;
  const isLoggedIn = t2 === undefined ? false : t2;
  const fullWidth = t3 === undefined ? false : t3;
  const gt = useGT();
  const showProgressBar = currentStep > 0;
  const t4 = fullWidth ? "" : undefined;
  const t5 = `flex flex-col bg-white ${fullWidth ? "w-full" : "w-full lg:flex-[0_0_55%]"}`;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      minWidth: 0
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const t7 = `mx-auto w-full px-5 pt-5 sm:px-6 lg:px-0 lg:pt-6 ${fullWidth ? "max-w-5xl" : "max-w-md"}`;
  let t8;
  if ($[1] !== backButtonLabel || $[2] !== currentStep || $[3] !== gt || $[4] !== onBack || $[5] !== showBackButton) {
    t8 = showBackButton && currentStep > 0 && onBack && <motion.button key="back-button" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION} onClick={onBack} className="flex items-center gap-0.5 text-sm font-medium text-black/50 transition-colors hover:text-black"><ChevronLeftIcon className="h-5 w-5" /><span>{backButtonLabel ?? gt("Back")}</span></motion.button>;
    $[1] = backButtonLabel;
    $[2] = currentStep;
    $[3] = gt;
    $[4] = onBack;
    $[5] = showBackButton;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t8) {
    t9 = <div className="absolute left-0 top-1/2 -translate-y-1/2"><AnimatePresence mode="wait">{t8}</AnimatePresence></div>;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Link href="/" className="block transition-opacity duration-150 ease-out hover:opacity-70"><Image src="/img/outfame-logo-full.svg" alt="Outfame" width={140} height={32} className="h-7 w-auto" priority={true} /></Link></div>;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== currentStep || $[11] !== isLoggedIn) {
    t11 = !isLoggedIn && currentStep === 0 && <motion.div key="sign-in" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION}><Link href="/login" className="text-sm font-medium text-black/50 transition-colors hover:text-black"><T>Sign in</T></Link></motion.div>;
    $[10] = currentStep;
    $[11] = isLoggedIn;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11) {
    t12 = <div className="absolute right-0 top-1/2 -translate-y-1/2"><AnimatePresence mode="wait">{t11}</AnimatePresence></div>;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t12 || $[16] !== t9) {
    t13 = <div className="relative flex h-8 items-center">{t9}{t10}{t12}</div>;
    $[15] = t12;
    $[16] = t9;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== currentStep || $[19] !== isLoggedIn || $[20] !== showProgressBar || $[21] !== totalSteps) {
    t14 = showProgressBar && <motion.div key="progress-bar" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={TRANSITION} className="mt-5"><ProgressBar currentStep={currentStep} totalSteps={totalSteps} isLoggedIn={isLoggedIn} /></motion.div>;
    $[18] = currentStep;
    $[19] = isLoggedIn;
    $[20] = showProgressBar;
    $[21] = totalSteps;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14) {
    t15 = <AnimatePresence mode="wait">{t14}</AnimatePresence>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t13 || $[26] !== t15 || $[27] !== t7) {
    t16 = <header className={t7}>{t13}{t15}</header>;
    $[25] = t13;
    $[26] = t15;
    $[27] = t7;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = showProgressBar ? "h-6" : "h-5";
  let t18;
  if ($[29] !== t17) {
    t18 = <div className={t17} />;
    $[29] = t17;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const t19 = `flex flex-1 flex-col px-5 sm:px-6 lg:px-10 ${currentStep === 1 ? "pb-48 lg:pb-8" : "pb-8"}`;
  const t20 = `mx-auto flex w-full flex-1 flex-col ${fullWidth ? "max-w-5xl" : "max-w-md"}`;
  let t21;
  if ($[31] !== fullWidth) {
    t21 = <SetupHelpLink wide={fullWidth} />;
    $[31] = fullWidth;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== children || $[34] !== t20 || $[35] !== t21) {
    t22 = <div className={t20}>{children}{t21}</div>;
    $[33] = children;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== t19 || $[38] !== t22) {
    t23 = <main className={t19}>{t22}</main>;
    $[37] = t19;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t16 || $[41] !== t18 || $[42] !== t23 || $[43] !== t5) {
    t24 = <div className={t5} style={t6}>{t16}{t18}{t23}</div>;
    $[40] = t16;
    $[41] = t18;
    $[42] = t23;
    $[43] = t5;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== fullWidth) {
    t25 = !fullWidth && <div className="hidden lg:block" style={{
      width: "45%",
      minWidth: 0
    }}><SocialProofPanel className="h-full" /></div>;
    $[45] = fullWidth;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t24 || $[48] !== t25 || $[49] !== t4) {
    t26 = <div className="flex min-h-screen w-full overflow-hidden" data-setup-wide={t4}>{t24}{t25}</div>;
    $[47] = t24;
    $[48] = t25;
    $[49] = t4;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  return t26;
}

/**
 * Fyxer.ai-style segmented progress bar
 *
 * Design specs:
 * - Full width of content area
 * - Rounded pill segments with gaps
 * - Completed + current = filled (black)
 * - Remaining = light gray (black/10)
 * - Smooth width animations
 */
function ProgressBar(t0) {
  const $ = _c(10);
  const {
    currentStep,
    totalSteps,
    isLoggedIn
  } = t0;
  let t1;
  if ($[0] !== isLoggedIn) {
    t1 = step => {
      if (isLoggedIn && step === 3) {
        return 2;
      }
      return step;
    };
    $[0] = isLoggedIn;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const getDisplayIndex = t1;
  let t2;
  if ($[2] !== currentStep || $[3] !== getDisplayIndex) {
    t2 = getDisplayIndex(currentStep);
    $[2] = currentStep;
    $[3] = getDisplayIndex;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const displayIndex = t2;
  let t3;
  if ($[5] !== totalSteps) {
    t3 = Array.from({
      length: totalSteps
    });
    $[5] = totalSteps;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== displayIndex || $[8] !== t3) {
    t4 = <div className="flex w-full items-center gap-1.5">{t3.map((_, index) => {
        const isCompleted = index < displayIndex;
        const isCurrent = index === displayIndex;
        const isFilled = isCompleted || isCurrent;
        return <motion.div key={index} className="h-1.5 flex-1 rounded-full" initial={false} animate={{
          backgroundColor: isFilled ? "#141414" : "rgba(0, 0, 0, 0.1)"
        }} transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        }} />;
      })}</div>;
    $[7] = displayIndex;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}